import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { CARD_BUTTON, CARD_COLORS, CardButton, PickerCard, stopEvent } from '../../view/picker_card.tsx';
import { TILE_FONT_FAMILY } from '../../view/theme.ts';
import { quickSetpoints, stepSetpoint, type SetpointRange } from './thermostat_tile.ts';

// The set-point picker (docs/specs/tiles.md §5): a card over the tile with the set point large in
// the middle, − and + beside it (held, they repeat), five whole degrees to jump to, and Cancel and
// Set. Nothing is sent before Set. Its range and step are the set-point command's (device-roles.md
// TH-3). Where the thermostat has modes, a row of them: a tap sends one at once (TH-4).

/** A held − or + repeats after this, then every `REPEAT_MS`. */
const REPEAT_AFTER_MS = 400;
const REPEAT_MS = 110;

export interface SetpointPickerProps {
    /** The tile's place on the page. */
    anchor: DOMRect;
    /** The tile's label, at the top. */
    title: string;
    /** Where it starts (`startingSetpoint`). */
    start: number;
    /** The set point as it is now, to tell whether Set changes anything; null when unknown. */
    current: number | null;
    /** The room's temperature as the tile shows it, with its unit; empty when unknown. */
    nowText: string;
    /** A set point's number as the card shows it (the tile's format). */
    format(value: number) : string;
    /** Its unit after the number: at half size, except a lone degree sign. */
    unit: string;
    range: SetpointRange;
    /** The modes it can be set to, with their words; none: no row of modes. */
    modes?: readonly { value: string; label: string }[] | undefined;
    /** The mode it is in now. */
    mode?: string | undefined;
    onMode?: ((value: string) => void) | undefined;
    onSet(value: number) : void;
    onClose() : void;
}

function StepButton({ label, steps, isDisabled, onStep }: { label: string; steps: number; isDisabled: boolean; onStep: (steps: number) => void }) : ReactNode {
    const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const stopRepeating = () : void => {
        clearTimeout(timer.current);
        timer.current = undefined;
    };
    useEffect(() => stopRepeating, []);
    useEffect(() => {
        if (isDisabled) {
            stopRepeating();
        }
    }, [isDisabled]);

    const press = (event: PointerEvent) : void => {
        stopEvent(event);
        if (isDisabled || event.button !== 0) {
            return;
        }
        onStep(steps);
        const repeat = () : void => {
            onStep(steps);
            timer.current = setTimeout(repeat, REPEAT_MS);
        };
        timer.current = setTimeout(repeat, REPEAT_AFTER_MS);
    };

    return (
        <button
            type="button"
            aria-label={label}
            disabled={isDisabled}
            onPointerDown={press}
            onPointerUp={stopRepeating}
            onPointerLeave={stopRepeating}
            onPointerCancel={stopRepeating}
            onContextMenu={(event) => event.preventDefault()}
            // A press steps on the pointer; a click from the keyboard (Enter, Space) has no pointer.
            onClick={(event) => {
                stopEvent(event);
                if (event.detail === 0 && !isDisabled) {
                    onStep(steps);
                }
            }}
            style={{
                ...CARD_BUTTON,
                width: 56,
                height: 56,
                flex: 'none',
                borderRadius: 28,
                background: CARD_COLORS.raised,
                fontSize: 30,
                lineHeight: '56px',
                opacity: isDisabled ? 0.35 : 1,
                cursor: isDisabled ? 'default' : 'pointer',
            }}
        >
            {steps < 0 ? '−' : '+'}
        </button>
    );
}

export function SetpointPicker({ anchor, title, start, current, nowText, format, unit, range, modes = [], mode, onMode, onSet, onClose }: SetpointPickerProps) : ReactNode {
    const [value, setValue] = useState(start);
    // Fixed when it opens: the quick choices do not move under a finger.
    const [quick] = useState(() => quickSetpoints(start, range));
    // Set has the focus, so that Enter sets.
    const setButton = useRef<HTMLButtonElement>(null);

    const step = (steps: number) : void => setValue((previous) => stepSetpoint(previous, steps, range));
    const confirm = () : void => {
        // Set without a change sends nothing.
        if (current !== null && Math.abs(current - value) < 0.01) {
            onClose();
        } else {
            onSet(value);
        }
    };

    // The arrow keys step, wherever the focus is.
    useEffect(() => {
        const onKey = (event: KeyboardEvent) : void => {
            const steps = event.key === 'ArrowUp' || event.key === 'ArrowRight' ? 1 : event.key === 'ArrowDown' || event.key === 'ArrowLeft' ? -1 : 0;
            if (steps !== 0) {
                event.preventDefault();
                setValue((previous) => stepSetpoint(previous, steps, range));
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [range]);

    return (
        <PickerCard anchor={anchor} title={title} aside={nowText === '' ? undefined : `now ${nowText}`} label="set point" initialFocus={setButton} onClose={onClose}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '18px 0' }}>
                <StepButton label="Lower" steps={-1} isDisabled={value <= range.min} onStep={step} />
                <div aria-live="polite" style={{ font: `56px/1 ${TILE_FONT_FAMILY}`, textAlign: 'center', flex: 1, minWidth: 0, whiteSpace: 'nowrap' }}>
                    {format(value)}
                    {/* A degree sign is small already; a unit with letters (°C) at half size. */}
                    {unit.trim() === '°' ? '°' : <span style={{ fontSize: 28, verticalAlign: 'top', marginLeft: 2 }}>{unit.trim()}</span>}
                </div>
                <StepButton label="Higher" steps={1} isDisabled={value >= range.max} onStep={step} />
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
                {quick.map((choice) => {
                    const isChosen = Math.abs(choice - value) < 0.01;
                    return (
                        <button
                            key={choice}
                            type="button"
                            aria-pressed={isChosen}
                            onClick={(event) => {
                                stopEvent(event);
                                setValue(choice);
                            }}
                            style={{ ...CARD_BUTTON, flex: 1, height: 40, borderRadius: 20, background: isChosen ? CARD_COLORS.accent : CARD_COLORS.raised, font: `18px ${TILE_FONT_FAMILY}` }}
                        >
                            {choice}°
                        </button>
                    );
                })}
            </div>

            {modes.length > 0 && onMode ? (
                // Sent at once, unlike the set point (TH-4); the tile shows it.
                <div role="group" aria-label="mode" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
                    {modes.map((each) => {
                        const isCurrent = each.value === mode;
                        return (
                            <button
                                key={each.value}
                                type="button"
                                aria-pressed={isCurrent}
                                onClick={(event) => {
                                    stopEvent(event);
                                    if (!isCurrent) {
                                        onMode(each.value);
                                    }
                                }}
                                style={{ ...CARD_BUTTON, flex: '1 1 auto', height: 40, padding: '0 12px', borderRadius: 20, background: isCurrent ? CARD_COLORS.accent : CARD_COLORS.raised, font: `16px ${TILE_FONT_FAMILY}` }}
                            >
                                {each.label}
                            </button>
                        );
                    })}
                </div>
            ) : null}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                <CardButton label="Cancel" onPress={onClose} />
                <CardButton label="Set" isMain buttonRef={setButton} onPress={confirm} />
            </div>
        </PickerCard>
    );
}
